import { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  Box,
  Alert,
  AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';

interface EditRockCountersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (consecutive: number, total: number) => Promise<void>;
  rockName: string;
  currentConsecutive: number;
  currentTotal: number;
}

const validationSchema = Yup.object({
  consecutive: Yup.number()
    .required('Consecutive count is required')
    .min(0, 'Count cannot be negative')
    .test('max-total', 'Consecutive count cannot exceed total count', function (value) {
      return value <= this.parent.total;
    }),
  total: Yup.number()
    .required('Total count is required')
    .min(0, 'Count cannot be negative'),
});

export function EditRockCountersModal({
  isOpen,
  onClose,
  onSave,
  rockName,
  currentConsecutive,
  currentTotal,
}: EditRockCountersModalProps) {
  const bgColor = useColorModeValue('blue.50', 'blue.900');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Rock Occurrence Counts</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={{
            consecutive: currentConsecutive,
            total: currentTotal,
          }}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              await onSave(values.consecutive, values.total);
              onClose();
            } catch (error) {
              setStatus('Failed to save changes. Please try again.');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Box bg={bgColor} borderWidth={1} borderColor="blue.200" borderRadius="md" p={3}>
                    <Text fontSize="sm" fontWeight="medium" color="blue.900">
                      {rockName}
                    </Text>
                  </Box>

                  <Field name="consecutive">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={form.errors.consecutive && form.touched.consecutive}
                      >
                        <FormLabel>Consecutive Meetings Off-Track</FormLabel>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Number of consecutive L10 meetings where this Rock has been off-track
                        </Text>
                        <NumberInput
                          {...field}
                          min={0}
                          onChange={(valueString) =>
                            form.setFieldValue('consecutive', parseInt(valueString) || 0)
                          }
                        >
                          <NumberInputField placeholder="0" />
                        </NumberInput>
                        <FormErrorMessage>{form.errors.consecutive}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="total">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={form.errors.total && form.touched.total}
                      >
                        <FormLabel>Total Times Off-Track</FormLabel>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Total cumulative times this Rock has been off-track across all meetings
                        </Text>
                        <NumberInput
                          {...field}
                          min={0}
                          onChange={(valueString) =>
                            form.setFieldValue('total', parseInt(valueString) || 0)
                          }
                        >
                          <NumberInputField placeholder="0" />
                        </NumberInput>
                        <FormErrorMessage>{form.errors.total}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {status && (
                    <Alert status="error">
                      <AlertIcon />
                      {status}
                    </Alert>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

